import { EXPORT_STATUS } from 'dynamo-daos/build/models/exports/exportRecord';
import { proxy } from 'valtio';
import { devtools } from 'valtio/utils';

import deleteFavorite from '@cartwhl/common/api/deleteFavorite';
import deleteJob from '@cartwhl/common/api/deleteJob';
import getExportJob from '@cartwhl/common/api/getExportJob';
import getJob from '@cartwhl/common/api/getJob';
import getMediaByID from '@cartwhl/common/api/getMediaByID';
import getMotionQualityCheck from '@cartwhl/common/api/getMotionQualityCheck';
import postFavorite from '@cartwhl/common/api/postFavorite';
import postFeedback from '@cartwhl/common/api/postFeedback';
import postJob from '@cartwhl/common/api/postJob';
import postJobExport, { ExportProps } from '@cartwhl/common/api/postJobExport';
import postJobFromExistingJob from '@cartwhl/common/api/postJobFromExistingJob';
import postJobsMediaUploadLink from '@cartwhl/common/api/postJobsMediaUploadLink';
import postMediaUploadLink, {
  MediaUploadPayload,
} from '@cartwhl/common/api/postMediaUploadLink';
import postProcessMedia from '@cartwhl/common/api/postProcessMedia';
import postSessionFromExistingJob from '@cartwhl/common/api/postSessionFromExistingJob';
import postSessionPrompt from '@cartwhl/common/api/postSessionPrompt';
import postWsAndFetchJob from '@cartwhl/common/api/postWsAndFetchJob';
import { StudioModalName } from '@cartwhl/common/stores/navigationStore';
import { Feedback, Job, RotationOffsets } from '@cartwhl/common/types';
import {
  findRecordPairByAnimationNumber,
  getLastSystemRecord,
} from '@cartwhl/common/utils/chatRecords';
import getCombinedJobStatus from '@cartwhl/common/utils/getCombinedJobStatus';
import { getHomepageUrl } from '@cartwhl/common/utils/getHomepageUrl';
import handleGenericErrors from '@cartwhl/common/utils/handleGenericErrors';
import {
  FileUploadState,
  FileUploadStatus,
} from '@cartwhl/ui/feature/FileUploader';

import {
  animationGenerationStore,
  animationPlayerStore,
  authStore,
  historyStore,
  navigationStore,
  noticesStore,
  projectStore,
  sceneSettingsStore,
  uiStore,
  userStore,
  webSocketStore,
} from 'stores';
import { getVideoDuration } from 'utils/getVideoDuration';
import uploadUtil from 'utils/uploadUtil';

import { fetchAvailableCharacters } from './characterPickerStore';
import { webSocket } from './webSocketStore';

// Must match 1-1 to the dynamo-daos/models/exports/exportRecord.ts enum
// It doesn't just get imported b/c we only import it as a devDependency
export enum EXPORT_TYPES {
  ABC = 'ABC',
  BVH = 'BVH',
  DAE = 'DAE',
  FBX_BLENDER = 'FBX-BLENDER',
  FBX_MAYA = 'FBX-MAYA',
  FBX_UNREAL = 'FBX-UNREAL',
  FBX_ROBLOX = 'FBX-ROBLOX',
  FBX_UNITY = 'FBX-UNITY',
  GLB = 'GLB',
  MA = 'MA',
  MB = 'MB',
  MP4 = 'MP4',
  PNG = 'PNG',
  USDZ = 'USDZ',
  QPOS = 'QPOS',
}

const DEFAULT_ANIMATION_COUNT = 3;
const getDefaultAnimation = (): Array<string> => {
  // Create an array filled with 1 to N
  const out: Array<string> = Array.from(
    { length: DEFAULT_ANIMATION_COUNT },
    (_, i) => (i + 1).toString(),
  );

  // Randomly swop elements, better results then sort( random () )
  out.forEach((_, i, a) => {
    const ri = Math.floor(Math.random() * DEFAULT_ANIMATION_COUNT);
    [a[i], a[ri]] = [a[ri], a[i]];
  });

  // Fill Array with file paths
  out.forEach((e, i, a) => {
    a[i] = `/default-animations${e}.bvh`;
  });

  return out;
};

export const DEFAULT_ANIMATIONS = getDefaultAnimation();

type AnimationGenerationStoreState = {
  animationPaths: Array<string>;
  exportEstimatedFinishTime: string;
  exportPath: string;
  exportSettings?: {
    animationNumber: string | number;
    format: EXPORT_TYPES;
    ikFeet: boolean;
    ikHands: boolean;
    includeMesh: boolean;
    keyframeSimplification: 'none' | 'reduce' | 'simplify';
    rotationOffsets?: RotationOffsets;
  };
  exportStartedAt: string;
  exportErrors?: string[];
  prompt: string;
  loading: {
    init: boolean;
    data: boolean;
    export: boolean;
    animation: boolean;
    mesh: boolean;
  };
  jobData?: Job;
  currentAnimationNumber: string;
  shareGLBFilePath?: string;
  shareVideoLink?: string;
  jobPolling?: NodeJS.Timeout;
  /*
   * finishedInBackground: A list of JobIDs that have been loaded in the background.
   * This is used to show a green dot next to jobs with updates in the history panel
   */
  finishedInBackground?: string[];
  newVariationsIndicator: { jobID: string; animationNumber: string }[];
  existingMediaExtension?: string;
  existingMediaID?: string;
  existingMediaName?: string;
  existingMediaURL?: string;
  mediaUpload?: FileUploadState;
  videoUpload?: FileUploadState;
  // Tracks prompt-based motion generation progress for showing an UploadNotice
  promptRequest?: FileUploadState;
};

export const state = proxy<AnimationGenerationStoreState>({
  animationPaths: [],
  exportEstimatedFinishTime: '',
  exportPath: '',
  exportSettings: undefined,
  exportStartedAt: '',
  exportErrors: [],
  prompt: '',
  loading: {
    init: false,
    data: false,
    export: false,
    animation: false,
    mesh: false,
  },
  jobData: undefined,
  currentAnimationNumber: '0',
  shareGLBFilePath: '',
  shareVideoLink: '',
  jobPolling: undefined,
  finishedInBackground: undefined,
  newVariationsIndicator: [],
  existingMediaExtension: undefined,
  existingMediaID: undefined,
  existingMediaName: undefined,
  existingMediaURL: undefined,
  mediaUpload: undefined, // used only when user wants to save a video to their profile
  videoUpload: undefined, // used for all video uploads
  promptRequest: undefined, // used for prompt-based generation jobs
});

if (process.env.NODE_ENV === 'development') {
  devtools(state, { name: 'AnimationGeneration', enabled: true });
}

///// Actions:

export async function init() {
  state.loading.init = true;

  const params = new URLSearchParams(window.location.search);
  const jobID = params.get('jobID');

  await webSocketStore.connect();

  // fetch characters after websockets are connected so that we can resub to currently uploading characters
  fetchAvailableCharacters(200);

  state.loading.init = false;

  if (params.has('prompt') && params.has('numFrames') && params.has('submit')) {
    // PromptInput.tsx will fill in the prompt and submit it for the user
  } else if (jobID) {
    await subscribeWsAndFetchJob(jobID);
  } else {
    state.animationPaths = DEFAULT_ANIMATIONS;
  }
}

export function setExportPath(value: string) {
  state.exportPath = value;
}

export function setExportSettings(
  value: AnimationGenerationStoreState['exportSettings'],
) {
  state.exportSettings = value;
}

export function setPrompt(prompt: string) {
  state.prompt = prompt;
}

export async function checkMotionQuality(prompt: string) {
  const { data, error } = await getMotionQualityCheck(prompt);
  if (error) {
    await handleGenericErrors(error);
    return undefined;
  }
  return data?.goodResults;
}

export function setFinishedInBackground(jobID: string) {
  state.finishedInBackground = [
    ...(state.finishedInBackground || ([] as string[])),
    jobID,
  ];
}

export async function setLoading(
  key: 'data' | 'animation' | 'mesh',
  loading: boolean,
) {
  state.loading[key] = loading;
}

export async function fetchJob(
  jobID: string,
  variationID?: string,
  loadDefaultVariation?: boolean,
) {
  state.loading.data = true;

  const { data, error } = await getJob(jobID);

  if (error) {
    const params = encodeURIComponent(encodeURIComponent(`jobID=${jobID}`));
    await handleGenericErrors(error, { redirectTo: `studio/home?${params}` });
    state.loading.data = false;
  } else if (data && data.deleted) {
    state.loading.data = false;
    noticesStore.error(
      'The motion you tried to load has been deleted. Please select a different motion!',
    );
    resetJobData();
  } else if (data) {
    setJobData(data);

    if (variationID) {
      selectAnimationVariation(Number(variationID));
    } else if (loadDefaultVariation) {
      const defaultVariationID = getDefaultVariationID(data);
      selectAnimationVariation(Number(defaultVariationID));
    }

    // Adding a delay to prevent flickering
    setTimeout(() => {
      state.loading.data = false;
    }, 100);
  }
}

/**
 * Fetch job data without setting it as the active job in the store
 * Used for loading job details in history panels without navigating to the job
 */
export async function fetchJobData(jobID: string) {
  const { data, error } = await getJob(jobID);

  if (error) {
    await handleGenericErrors(error);
    return { data: undefined, error };
  } else if (data && data.deleted) {
    noticesStore.error(
      'The motion you tried to load has been deleted. Please select a different motion!',
    );
    return { data: undefined, error: 'Job deleted' };
  } else if (data) {
    return { data, error: undefined };
  }

  return { data: undefined, error: 'Unknown error' };
}

export function selectAnimationVariation(
  animationNumber: number,
  resetSceneSettings = true,
) {
  if (!state.jobData) return;
  if (!state.jobData.animationNumbers) {
    return;
  }
  if (!state.jobData.animationNumbers.includes(animationNumber)) {
    noticesStore.error('Variation not found!');
    return;
  }

  const variation = state.jobData.variationInfoByID[animationNumber.toString()];

  if (variation.bvhURL) {
    state.animationPaths = [variation.bvhURL];
  }

  // Cleanup other stores and load the new scene settings
  animationPlayerStore.reset(variation.trimFrameStart, variation.trimFrameEnd);

  if (resetSceneSettings) {
    sceneSettingsStore.reset(state.jobData.sceneSettings);
  } else {
    // If we're not resetting scene settings, we need to save them
    // This is used when we are creating a new session from a browse motion
    sceneSettingsStore.save();
  }
  animationPlayerStore.setControls('player');

  const params = new URLSearchParams(window.location.search);
  params.set('jobID', state.jobData.jobID);
  params.set('animationNumber', animationNumber.toString());

  state.currentAnimationNumber = animationNumber.toString();

  // Get the current path to maintain the route (pose mode vs home)
  const currentPath = window.location.pathname;
  navigationStore.navigate(`${currentPath}?${params.toString()}`);
}

export async function submitJobFromExistingMotion(props: {
  currentAnimationNumber?: string | number;
  existingMotionAnimationNumber: number;
  existingMotionJobID: string;
  trimStart?: number;
  trimEnd?: number;
  newSession?: boolean;
}) {
  animationGenerationStore.setPrompt('');
  animationGenerationStore.setLoading('animation', true);
  if (authStore.state.authStatus === 'anonymous') {
    noticesStore.error('You must be logged in to animate!');
    animationGenerationStore.setLoading('animation', false);
    return;
  }
  if (
    !webSocketStore.webSocket ||
    !webSocketStore.webSocket.connectionID ||
    !webSocketStore.webSocket.endpoint
  ) {
    noticesStore.error(
      'Websockets are not connected. Try refreshing your page.',
    );
    animationGenerationStore.setLoading('animation', false);
    return;
  }

  // if you have an existing session loaded and don't want a new one, use postSessionFromExistingJob
  // otherwise, use postJobFromExistingJob
  const { data, error } =
    state.jobData && !props.newSession
      ? await postSessionFromExistingJob(state.jobData.jobID, {
        existingAnimationNumber: props.existingMotionAnimationNumber,
        existingJobID: props.existingMotionJobID,
        selectedAnimationNumber: Number(props.currentAnimationNumber),
        selectedTrimStart: animationPlayerStore.state.trimFrameStart,
        selectedTrimEnd: animationPlayerStore.state.trimFrameEnd,
        trimStart: props.trimStart,
        trimEnd: props.trimEnd,
        wsConnectionID: webSocketStore.webSocket.connectionID,
        wsHttpEndpoint: webSocketStore.webSocket.endpoint,
      })
      : await postJobFromExistingJob({
        existingAnimationNumber: props.existingMotionAnimationNumber,
        existingJobID: props.existingMotionJobID,
        existingTrimStartFrame: props.trimStart || undefined,
        existingTrimEndFrame: props.trimEnd || undefined,
        favoriteNewJob: false,
        hideFromHistory: false,
        wsConnectionID: webSocketStore.webSocket.connectionID,
        wsHttpEndpoint: webSocketStore.webSocket.endpoint,
      });

  // TODO: Submitting a prompt-based job checks for auth and has
  // an auth, then submit mechanism. Need to figure out what that looks like for this flow
  if (error) {
    const currentPath = window.location.pathname;
    await handleGenericErrors(error, { redirectTo: currentPath });
    animationGenerationStore.setLoading('animation', false);
    return { success: false };
  } else if (data) {
    const lastSystemRecord = getLastSystemRecord(data);
    const animationNumber =
      lastSystemRecord?.animationNumbers?.at(-1)?.at(0) || 0;

    setJobData(data);

    if (animationNumber !== undefined) {
      selectAnimationVariation(animationNumber, false);
    }

    historyStore.updateOrAddJob(data.jobID, data);

    return { success: true };
  }
}

export async function submitJobFromPrompt(props: {
  numFrames: number;
  prompt: string;
  moreVariations?: boolean;
  currentAnimationNumber?: string;
}) {
  if (authStore.state.authStatus === 'anonymous') {
    noticesStore.error('You must be logged in to animate!');
    return {
      success: false,
      error: 'You must be logged in to animate!',
    };
  }
  const min = 1;
  if (props.prompt.length < min) {
    noticesStore.error(`Must submit a prompt of at least ${min} characters`);
    return {
      success: false,
      error: `Must submit a prompt of at least ${min} characters`,
    };
  }

  const { data, error } = await getMotionQualityCheck(props.prompt);
  if (error) {
    await handleGenericErrors(error);
    return {
      success: false,
      error: error,
    };
  }
  if (data?.goodResults) {
    return submitJobFromPromptConfirmed(props);
  }

  navigationStore.setModal(StudioModalName.WARN_BAD_MOTION_PREDICTED, {
    submit: () => {
      submitJobFromPromptConfirmed(props);
    },
  });
}

export async function submitJobFromPromptConfirmed(props: {
  numFrames: number;
  prompt: string;
  moreVariations?: boolean;
  currentAnimationNumber?: string;
}) {
  if (!webSocket || !webSocket.connectionID || !webSocket.endpoint) {
    noticesStore.error(
      'Websockets are not connected. Try refreshing your page.',
    );
    return {
      success: false,
      error: 'Websockets are not connected. Try refreshing your page.',
    };
  }

  const { data, error } = state.jobData
    ? await postSessionPrompt(state.jobData.jobID, {
      moreVariations: Boolean(props.moreVariations),
      prompt: props.prompt,
      selectedAnimationNumber: Number(props.currentAnimationNumber),
      trimStart: animationPlayerStore.state.trimFrameStart,
      trimEnd: animationPlayerStore.state.trimFrameEnd,
      wsConnectionID: webSocket.connectionID,
      wsHttpEndpoint: webSocket.endpoint,
    })
    : await postJob({
      prompt: props.prompt,
      numFrames: props.numFrames,
      wsConnectionID: webSocket.connectionID,
      wsHttpEndpoint: webSocket.endpoint,
      sceneSettings: sceneSettingsStore.transformStateToApiShape(),
    });

  const redirectParams = new URLSearchParams({
    numFrames: props.numFrames.toString(),
    prompt: props.prompt,
    submit: 'true',
  });

  // INFO: Double encoding is intentional so that it is handled correctly passing
  // through Stytch's RedirectURL mechanism
  const encodedParams = encodeURIComponent(
    encodeURIComponent(redirectParams.toString()),
  );

  if (error) {
    const currentPath = window.location.pathname;
    await handleGenericErrors(error, {
      redirectTo: `${currentPath}?${encodedParams}`,
    });
    return { success: false, error: error };
  } else if (data) {
    const lastSystemRecord = getLastSystemRecord(data);

    const isNewSession =
      state.jobData?.jobID && data.jobID !== state.jobData.jobID;
    const isStitching =
      !isNewSession && lastSystemRecord?.animationNumbers?.length === 1;

    let animationNumber: number | undefined = undefined;

    if (isNewSession) {
      navigationStore.setModal(StudioModalName.NEW_SESSION);
      animationNumber = 0;
    } else if (isStitching) {
      animationNumber = lastSystemRecord?.animationNumbers.at(-1)?.at(0) || 0;
    }

    // Track prompt-based generation progress for UploadNotice
    const combinedStatus = getCombinedJobStatus(data);
    state.promptRequest = {
      uploadID: data.jobID,
      status: combinedStatus === 'QUEUED' ? 'QUEUED' : 'PROCESSING',
      createdAt: data.lastGenerationRequestAt,
      errorReasons: undefined,
      fileTransferPercent: 0,
      processingPercentage: 0,
      estimatedProcessingTime: data.estimatedProcessingTime
        ? data.estimatedProcessingTime * 1000
        : undefined,
    };
    uploadUtil(state.promptRequest).startProcessingCounter();

    setJobData(data);

    if (animationNumber !== undefined) {
      selectAnimationVariation(animationNumber);
    }

    historyStore.updateOrAddJob(data.jobID, data);
    uiStore.setPromptFocus(false);

    return { success: true, error: undefined };
  }
  return { success: false, error: 'Unknown error' };
}

export async function subscribeWsAndExportJob({
  animationNumber,
  format,
  ikFeet,
  ikHands,
  includeMesh,
  jobID,
  keyframeSimplification,
  rotationOffsets,
  turnstileToken,
}: {
  animationNumber: string | number;
  format: EXPORT_TYPES;
  ikFeet: boolean;
  ikHands: boolean;
  includeMesh: boolean;
  jobID: string;
  keyframeSimplification: 'none' | 'reduce' | 'simplify';
  rotationOffsets?: RotationOffsets;
  turnstileToken?: string;
}) {
  if (!webSocket || !webSocket.connectionID || !webSocket.endpoint) {
    noticesStore.error(
      'Websockets are not connected. Try refreshing your page.',
    );
    return;
  }
  clearExportErrors();
  state.loading.export = true;

  const props: ExportProps = {
    animationNumber,
    characterID: sceneSettingsStore.state.characterSettings.charUploadID,
    format,
    frameRate: sceneSettingsStore.state.playbackSettings.frameRate,
    frameStepSize: sceneSettingsStore.state.playbackSettings.frameStepSize,
    handID: sceneSettingsStore.state.characterSettings.staticHands,
    ikFeet: ikFeet,
    ikHands: ikHands,
    includeMesh,
    keyframeSimplification,
    moveInPlace: !sceneSettingsStore.state.characterSettings.enableRootMotion,
    jobID: jobID,
    rotationOffsets,
    turnstileToken,
    wsConnectionID: webSocket.connectionID,
    wsHttpEndpoint: webSocket.endpoint,
  };
  // Add skinHex if it's not the default value.
  if (sceneSettingsStore.state.characterSettings.skinHex !== 'default') {
    props.skinHex = sceneSettingsStore.state.characterSettings.skinHex;
  }
  if (sceneSettingsStore.state.characterSettings.faceExpression) {
    props.faceExpression =
      sceneSettingsStore.state.characterSettings.faceExpression;
  }
  if (
    animationPlayerStore.state.trimFrameStart !== undefined ||
    animationPlayerStore.state.trimFrameEnd !== undefined
  ) {
    const baseTrimStart = animationPlayerStore.state.trimFrameStart ?? 0;
    const baseTrimEnd =
      animationPlayerStore.state.trimFrameEnd ??
      animationPlayerStore.state.duration.originalFrames;
    const baseFpsRate = Math.round(
      animationPlayerStore.state.duration.originalFrames /
      animationPlayerStore.state.duration.originalSeconds,
    );
    const fpsAdjustFactor = props.frameRate / baseFpsRate;

    const baseTrimFPSFactored = Math.floor(baseTrimStart * fpsAdjustFactor);
    const baseTrimEndFPSFactored = Math.floor(baseTrimEnd * fpsAdjustFactor);
    props.trimStart = baseTrimFPSFactored;
    props.trimEnd = baseTrimEndFPSFactored;
  }

  const { data, error } = await postJobExport(props);

  if (data) {
    if ('file' in data) {
      state.exportPath = data.file;
      state.loading.export = false;
      return data.file;
    } else if ('estimatedFinishTime' in data && 'exportStartedAt' in data) {
      state.exportEstimatedFinishTime = data.estimatedFinishTime;
      state.exportStartedAt = data.exportStartedAt;
    } else {
      state.loading.export = false;
    }
  } else if (error) {
    await handleGenericErrors(error);
    state.loading.export = false;

    // if error includes Please verify your email first before downloading motions
    if (
      error.message.includes(
        'Please verify your email first before downloading motions',
      )
    ) {
      // redirect them to the profile page.
      // handleGenericErrors will handle showing them a message
      setTimeout(() => {
        window.location.href = getHomepageUrl('/profile/account-details');
      }, 2500);
    }
  }
}

async function subscribeWsAndFetchJob(jobId: string) {
  if (!webSocket || !webSocket.connectionID || !webSocket.endpoint) {
    noticesStore.error(
      'Websockets are not connected. Try refreshing your page.',
    );
    return;
  }
  state.loading.data = true;

  const { data, error } = await postWsAndFetchJob(jobId, {
    wsConnectionID: webSocket.connectionID,
    wsHttpEndpoint: webSocket.endpoint,
  });

  if (data && data.deleted) {
    state.loading.data = false;
    noticesStore.error(
      'The motion you tried to load has been deleted. Please select a different motion!',
    );
    resetJobData();
  } else if (data) {
    setJobData(data);

    // Select the correct variation:
    const animationNumber = Number(getDefaultVariationID(data));
    selectAnimationVariation(animationNumber);

    // Adding a delay to prevent flickering
    setTimeout(() => {
      state.loading.data = false;
    }, 100);
  } else if (error) {
    await handleGenericErrors(error);
    state.loading.data = false;
  }
}

// Helper function that does a few things more than just setting the data
// It is recommended to always use this function to set jobData
function setJobData(job: Job) {
  const prevJobId = state.jobData?.jobID;
  const prevStatus = getCombinedJobStatus(state.jobData);

  state.jobData = job;
  const lastRecord = getLastSystemRecord(job);
  const status = getCombinedJobStatus(state.jobData);

  if (
    job.jobID === prevJobId &&
    (prevStatus === 'GENERATING_BVH' || prevStatus === 'GENERATING_PK') &&
    status === 'COMPLETED' &&
    (lastRecord?.animationNumbers?.length || 0) > 1
  ) {
    // variations completed...
    noticesStore.success('New variations created');
    const animationNumber = lastRecord?.animationNumbers?.at(-1)?.at(0) || 0;
    if (animationNumber) selectAnimationVariation(animationNumber);

    lastRecord?.animationNumbers?.at(-1)?.forEach((animationNumber) => {
      state.newVariationsIndicator.push({
        jobID: job.jobID,
        animationNumber: animationNumber.toString(),
      });
    });
  }

  if (
    (status === 'COMPLETED' || status === 'FAILED') &&
    state.promptRequest &&
    state.promptRequest.uploadID === job.jobID
  ) {
    removePromptRequest();
  }

  // Recover videoUpload object after refresh
  if (
    lastRecord?.path.startsWith('mu') &&
    (status === 'QUEUED' ||
      status === 'GENERATING_PK' ||
      status === 'GENERATING_BVH')
  ) {
    if (!state.videoUpload) {
      state.videoUpload = {
        uploadID: job.jobID,
        status: status === 'QUEUED' ? 'QUEUED' : 'PROCESSING',
        createdAt: job.lastGenerationRequestAt,
        errorReasons: undefined,
        fileTransferPercent: 0,
        processingPercentage: 0,
        estimatedProcessingTime: job.estimatedProcessingTime * 1000,
      };
      uploadUtil(state.videoUpload).startProcessingCounter();
    } else {
      state.videoUpload.estimatedProcessingTime =
        job.estimatedProcessingTime * 1000;
    }
  } else {
    state.videoUpload = undefined;
  }

  // Recover promptRequest (prompt-based generation) after refresh when not a media upload
  if (
    !lastRecord?.path.startsWith('mu') &&
    (status === 'QUEUED' ||
      status === 'GENERATING_PK' ||
      status === 'GENERATING_BVH')
  ) {
    if (!state.promptRequest) {
      state.promptRequest = {
        uploadID: job.jobID,
        status: status === 'QUEUED' ? 'QUEUED' : 'PROCESSING',
        createdAt: job.lastGenerationRequestAt,
        errorReasons: undefined,
        fileTransferPercent: 0,
        processingPercentage: 0,
        estimatedProcessingTime: job.estimatedProcessingTime * 1000,
      };
      uploadUtil(state.promptRequest).startProcessingCounter();
    } else {
      state.promptRequest.estimatedProcessingTime =
        job.estimatedProcessingTime * 1000;
    }
  }

  // When opening a job then remove it from finishedInBackground
  state.finishedInBackground = state.finishedInBackground?.filter(
    (id) => id !== job.jobID,
  );
}

/**
 * Resets to the initial state (i.e. when the user first opens the app and no job is loaded)
 */
export async function resetJobData() {
  state.jobData = undefined;
  state.currentAnimationNumber = '0';
  state.animationPaths = DEFAULT_ANIMATIONS;
  state.prompt = '';
  animationPlayerStore.reset();
  sceneSettingsStore.reset();

  // Get the current path to maintain the route (pose mode vs home)
  const currentPath = window.location.pathname;
  navigationStore.navigate(currentPath);
}

export async function setFavorite(
  jobID: string,
  animationNumber: string | number,
  desiredStatus: boolean,
  hideFromHistory = false,
  loadJobData = true,
) {
  if (
    state.jobData &&
    state.jobData.createdBy === authStore.state.userID &&
    !jobID.startsWith('mogen-job-browse')
  ) {
    if (desiredStatus) {
      const { data, error } = await postFavorite(jobID, +animationNumber);
      if (error) {
        handleGenericErrors(error);
      } else if (data) {
        historyStore.setFavorite(jobID, animationNumber, true);
        if (state.jobData.jobID === jobID) {
          state.jobData.variationInfoByID[animationNumber].favorite = true;
        }
      }
      return { data, error };
    } else {
      const { data, error } = await deleteFavorite(jobID, +animationNumber);
      if (error) {
        handleGenericErrors(error);
      } else if (data) {
        historyStore.setFavorite(jobID, animationNumber, false);
        if (state.jobData.jobID === jobID) {
          state.jobData.variationInfoByID[animationNumber].favorite = false;
        }
      }
      return { data, error };
    }
  } else {
    if (desiredStatus) {
      // You favorited somebody else's job, we need to actually clone it and favorite it
      const trimStart = animationPlayerStore.state.trimFrameStart;
      const trimEnd = animationPlayerStore.state.trimFrameEnd;
      const { data, error } = await postJobFromExistingJob({
        existingAnimationNumber: Number(animationNumber),
        existingJobID: jobID,
        existingTrimStartFrame: trimStart,
        existingTrimEndFrame: trimEnd,
        favoriteNewJob: true,
        hideFromHistory: hideFromHistory,
        wsConnectionID: webSocket?.connectionID ?? '',
        wsHttpEndpoint: webSocket?.endpoint ?? '',
      });

      if (error) {
        const currentPath = window.location.pathname;
        await handleGenericErrors(error, { redirectTo: currentPath });
        return { success: false };
      } else if (data) {
        const lastSystemRecord = getLastSystemRecord(data);
        const animationNumber =
          lastSystemRecord?.animationNumbers?.at(-1)?.at(0) || 0;

        if (loadJobData) {
          setJobData(data);

          if (animationNumber !== undefined) {
            selectAnimationVariation(animationNumber);
          }

          historyStore.updateOrAddJob(data.jobID, data);
        }

        return { success: true };
      }
    } else {
      // You unfavorited somebody else's job.
      // TODO: Can we find this in your favorites and remove it for you? today we don't have a way to do this.
      return { success: true };
    }
  }
}

export async function setFeedback(feedback: Feedback) {
  const { data, error } = await postFeedback(feedback);

  if (error) {
    handleGenericErrors(error);
  } else if (data && state.jobData && state.jobData.jobID === data.jobID) {
    const animationNumber = data.animationNumber.toString();
    const variation = state.jobData.variationInfoByID[animationNumber];
    if (variation) {
      variation.feedback = data.sentiment;
    }
    historyStore.setFeedback(data.jobID, data.animationNumber, data.sentiment);
  } else if (data) {
    historyStore.setFeedback(data.jobID, data.animationNumber, data.sentiment);
  }
}

export async function toggleFavorite(animationNumber: string | number) {
  if (state.jobData) {
    const variation = state.jobData.variationInfoByID[animationNumber];
    if (variation) {
      if (variation.favorite) {
        return await deleteFavorite(state.jobData.jobID, +animationNumber);
      } else {
        return await postFavorite(state.jobData.jobID, +animationNumber);
      }
    }
  }
}

export async function removeFromNewVariationsIndicator(
  jobID: string,
  animationNumber: string,
) {
  state.newVariationsIndicator = state.newVariationsIndicator.filter(
    (obj) => !(obj.jobID === jobID && obj.animationNumber === animationNumber),
  );
}

export async function setSessionName(jobID: string, sessionName: string) {
  if (state.jobData?.jobID === jobID && sessionName.trim()) {
    state.jobData.sessionName = sessionName.trim();
  }
}

export async function deleteJobFromBackend(
  jobID: string,
  skipNotice?: boolean,
) {
  console.log('deleting job', jobID);
  const { error } = await deleteJob(jobID);
  if (error) {
    await handleGenericErrors(error);
  } else if (!skipNotice) {
    noticesStore.success('Job deleted!');
    // If we're currently viewing the deleted job, reset to default state
    if (state.jobData?.jobID === jobID) {
      resetJobData();
    }
  }
}

////// Video Upload:

export async function beginVideoUpload(file: File, mediaID?: string) {
  if (!webSocket || !webSocket.connectionID || !webSocket.endpoint) {
    console.error('Websockets are not connected.');
    return;
  }

  const filename = file.name;
  const fileExtension = filename.split('.').pop()?.toLowerCase() || '';
  const videoDuration = await getVideoDuration(file);

  state.videoUpload = {
    uploadID: '',
    status: 'UPLOADING',
    createdAt: new Date().toISOString(),
    errorReasons: undefined,
    fileTransferPercent: 0,
    processingPercentage: 0,
  };
  const { data, error } = await postJobsMediaUploadLink({
    extension: fileExtension,
    jobID: state.jobData?.jobID,
    mediaID: mediaID,
    wsConnectionID: webSocket.connectionID,
    wsHttpEndpoint: webSocket.endpoint,
  });

  if (error) {
    state.videoUpload.status = 'ERROR';
    await handleGenericErrors(error);
  } else if (data) {
    state.videoUpload.uploadID = data.jobID || '';

    await uploadUtil(state.videoUpload).upload(data.mediaUploadURL, file);

    if (state.videoUpload) {
      const { data: pData, error: pError } = await postProcessMedia(
        data.jobID,
        {
          extension: fileExtension,
          filename: filename,
          mediaID: mediaID,
          selectedAnimationNumber: state.currentAnimationNumber,
          videoDuration,
          wsConnectionID: webSocket.connectionID,
          wsHttpEndpoint: webSocket.endpoint,
        },
      );

      if (pData) {
        const lastSystemRecord = getLastSystemRecord(pData);
        const animationNumber =
          lastSystemRecord?.animationNumbers?.at(-1)?.at(0) || 0;
        state.videoUpload.estimatedProcessingTime =
          pData.estimatedProcessingTime * 1000;

        if (navigationStore.state.modal.type === StudioModalName.VIDEO_UPLOAD) {
          navigationStore.closeModal();
        }
        setJobData(pData);
        if (animationNumber !== undefined) {
          selectAnimationVariation(animationNumber, false);
        }
      } else if (pError) {
        navigationStore.closeModal();
        if (pError.message.includes('Insufficient spins available for')) {
          //noticesStore.getMoreSpins();
          navigationStore.setModal(StudioModalName.READY_FOR_MORE_SPINS);
        } else {
          state.videoUpload.status = 'ERROR';
          state.videoUpload.errorReasons = [
            'There was an error trying to initiate the video processing, please try again or reach out for help',
            `Error: ${pError.message}`,
          ];
        }
      }
    }
  }
}

export function finishVideoProcessing(status: FileUploadStatus) {
  if (!state.videoUpload) return;

  state.videoUpload.status = status;
}

export function removeUpload() {
  state.videoUpload = undefined;
}

export function finishPromptProcessing(status: FileUploadStatus) {
  if (!state.promptRequest) return;
  state.promptRequest.status = status;
  if (status === 'ERROR') {
    noticesStore.error('Motion request failed, please try again.');
  }
}

export function removePromptRequest() {
  state.promptRequest = undefined;
}

export async function cancelPromptRequest() {
  if (!state.promptRequest?.uploadID) return;
  const jobID = state.promptRequest.uploadID;
  removePromptRequest();
  resetJobData();
  await deleteJobFromBackend(jobID, true);
  noticesStore.success('Animation request has been cancelled!');
}

export async function saveMediaToProfile(
  file: File,
  payload: MediaUploadPayload,
) {
  const { data, error } = await postMediaUploadLink(payload);
  if (data) {
    state.mediaUpload = {
      uploadID: data.mediaID,
      status: 'PROCESSING',
      createdAt: new Date().toISOString(),
      errorReasons: undefined,
      fileTransferPercent: 0,
      estimatedProcessingTime: undefined,
    };
    await uploadUtil(state.mediaUpload).upload(data.mediaUploadURL, file);

    // TODO: We have a thumbnail URL that can be used here as well, should we push something to it?
    // we don't have a way to show it in the UI yet

    return data.mediaID;
  } else if (error) {
    await handleGenericErrors(error);
  }
}

export function clearMediaData() {
  state.existingMediaExtension = undefined;
  state.existingMediaID = undefined;
  state.existingMediaName = undefined;
  state.existingMediaURL = undefined;
}

export function setExistingMediaExtension(value: string | undefined) {
  state.existingMediaExtension = value;
}

export function setExistingMediaID(value: string | undefined) {
  state.existingMediaID = value;
}

export function setExistingMediaName(value: string | undefined) {
  state.existingMediaName = value;
}

export function setExistingMediaURL(value: string | undefined) {
  state.existingMediaURL = value;
}

export async function fetchExistingMedia(mediaID: string) {
  const { data, error } = await getMediaByID(mediaID);
  if (error) {
    await handleGenericErrors(error);
    return;
  }
  if (data) {
    state.existingMediaExtension = data.fileExtension;
    state.existingMediaID = data.mediaID;
    state.existingMediaName = data.mediaName;
    state.existingMediaURL = data.mediaDownloadURL;
    return data;
  }
}

export async function pollExportStatus(enableAutoDownload: boolean = false) {
  const jobID = animationGenerationStore.state.jobData?.jobID;
  const animationNumber = animationGenerationStore.state.currentAnimationNumber;
  const characterID = sceneSettingsStore.state.characterSettings.charUploadID;

  if (!jobID || !characterID) return;

  try {
    // Fetch the latest job data to check the export status
    const { data, error } = await getExportJob(
      jobID,
      animationNumber,
      characterID,
    );

    if (error) {
      console.error('Error polling for job export status:', error);
      return;
    }

    if (!data) {
      console.warn(`Export for jobID ${jobID} not found while polling.`);
      return;
    }

    switch (data.status) {
      case EXPORT_STATUS.QUEUED:
      case EXPORT_STATUS.GENERATING:
        if (!state.exportStartedAt) {
          state.exportStartedAt = data.createdAt;
        }
        if (!state.loading.export) {
          state.loading.export = true;
          state.exportEstimatedFinishTime = data.estimatedFinishTime;
        }
        break;
      case EXPORT_STATUS.COMPLETED:
        if (enableAutoDownload) {
          state.exportPath = data.mostRecentDownloadPath;

          // Refetch userInfo to keep the amount of spins up to date
          userStore.refetchUserInfo();
        }
        if (state.loading.export) {
          state.exportEstimatedFinishTime = new Date().toISOString();
          state.loading.export = false;
        }
        break;
      case EXPORT_STATUS.FAILED:
      case EXPORT_STATUS.REJECTED:
        setExportErrors([
          `Export ${data.status.toLocaleLowerCase()}. Please try again.`,
        ]);
        if (state.loading.export) {
          state.exportEstimatedFinishTime = new Date().toISOString();
          state.loading.export = false;
        }
        break;
      default:
        console.error('Invalid export status: ', data.status);
    }
  } catch (error) {
    console.error('Error in getting export job: ', error);
  }
}

export function handleVideoUploadFailed() {
  // get current chat history objects
  const pair = findRecordPairByAnimationNumber(
    state.currentAnimationNumber,
    state.jobData,
  );
  const referenceJobId = pair?.[0].jobID;
  const referenceAnimationNumber = pair?.[0].animationNumber?.toString();
  if (
    referenceJobId &&
    referenceAnimationNumber !== undefined &&
    // If they have a different job loaded, or this is a follow-up motion, we need to refresh
    // the job data.
    (referenceJobId !== state.jobData?.jobID ||
      referenceAnimationNumber !== state.currentAnimationNumber ||
      Number(referenceAnimationNumber) > 0)
  ) {
    fetchJob(referenceJobId, referenceAnimationNumber);
  } else {
    resetJobData();
  }
}

export function clearExportErrors() {
  state.exportErrors = [];
}

//// HELPER FUNCTIONS

function getDefaultVariationID(jobData: Job): string {
  const params = new URLSearchParams(window.location.search);
  const fromParams = params.get('animationNumber');
  if (fromParams) {
    return fromParams;
  }

  const keys = Object.keys(jobData.variationInfoByID);
  return keys.at(-4) || '0';
}

function setExportErrors(errorMessages: string[]) {
  state.exportErrors = [...errorMessages];
}

//// WEBSOCKET EVENTS

webSocketStore.on('wake', () => {
  // If websockets wake up, and the job is not completed, we refetch the job and re-subscribe to updates
  if (state.jobData && getCombinedJobStatus(state.jobData) !== 'COMPLETED') {
    subscribeWsAndFetchJob(state.jobData.jobID);
  }
});

webSocketStore.on('GENERATING_PK', async (data) => {
  const projectData = projectStore.state.currentProject.data;
  if (projectData?.projectID) {
    // if it's part of a project we want to do nothing with this message.
    return;
  }

  // TODO: In the future if there is a queue we're going to want to rethink this.
  // cause we wouldn't want to force them back to a job if they are somewhere else.
  await fetchJob(data.jobID, undefined, true);
  // Update prompt-based notice to processing
  if (state.promptRequest && state.promptRequest.uploadID === data.jobID) {
    state.promptRequest.status = 'PROCESSING';
  } else if (state.promptRequest === undefined) {
    let i = 0;
    // Sometimes websockets come in before the state has been updated.
    // so if promptRequest is still undefined we wait until it gets set.
    while (state.promptRequest === undefined && i < 50) {
      i++;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    if (state.promptRequest) {
      (state.promptRequest as any).status = 'PROCESSING';
    }
  }
});

webSocketStore.on('BVH_READY', async (data) => {
  const projectData = projectStore.state.currentProject.data;
  if (projectData?.projectID) {
    // if it's part of a project we want to do nothing
    return;
  }

  if (state.jobData && state.jobData.jobID === data.jobID) {
    // We only fetch the job if the user is currently looking at the same job.
    // If users have navigated elsewhere we don't want to force load the job
    // as that's a jarring experience. Instead we notify that the job is ready.
    await fetchJob(data.jobID, undefined, true);

    finishVideoProcessing('SUCCESS');
    finishPromptProcessing('SUCCESS');
  } else {
    // FUTURE TODO: Change noticesStore so that this can link to the job
    // instead of only being dismissable.
    if (!state.finishedInBackground?.includes(data.jobID)) {
      noticesStore.success(`New motions for ${data.prompt} are ready to view!`);
      setFinishedInBackground(data.jobID);
    }
    if (state.promptRequest && state.promptRequest.uploadID === data.jobID) {
      removePromptRequest();
    }
  }
  // Refetch userInfo to keep the amount of spins up to date
  userStore.refetchUserInfo();
});

webSocketStore.on('PROCESSING_VIDEO', async (data) => {
  const projectData = projectStore.state.currentProject.data;
  if (projectData?.projectID) {
    // if it's part of a project we want to do nothing
    return;
  }
  // Set the video upload status to processing (i.e. not in queue anymore)
  await fetchJob(data.jobID, undefined, true);
  if (state.videoUpload) {
    state.videoUpload.status = 'PROCESSING';
  }
});

webSocketStore.on('VIDEO_FLAGGED', async () => {
  if (state.videoUpload) {
    state.videoUpload.status = 'ERROR';
  }
  navigationStore.setModal(StudioModalName.FLAGGED_VIDEO);
});

webSocketStore.on('FAILED', () => {
  finishVideoProcessing('ERROR');
  finishPromptProcessing('ERROR');

  handleVideoUploadFailed();
});

webSocketStore.on('EXPORT_READY', (data) => {
  if (!data.jobID) return;

  if (state.exportSettings) {
    const { format, includeMesh } = state.exportSettings;
    let meshStr = '';
    // BVH and QPOS are always meshless, .MA and .MB are always with-mesh
    if (format === EXPORT_TYPES.BVH || format === EXPORT_TYPES.QPOS) {
      meshStr = '-meshless';
    } else if (format === EXPORT_TYPES.MA || format === EXPORT_TYPES.MB) {
      meshStr = '-with-mesh';
    } else {
      meshStr = includeMesh ? '-with-mesh' : '-meshless';
    }
    const key = `${format.toLowerCase()}${meshStr}`;
    const downloadURL = data.downloadURLs[key];
    state.exportEstimatedFinishTime = new Date().toISOString();
    state.exportPath = downloadURL;
    setTimeout(() => {
      if (state.loading.export) {
        state.loading.export = false;
      }
    }, 500);
  }
  // Refetch userInfo to keep the amount of spins up to date
  userStore.refetchUserInfo();
});

webSocketStore.on('EXPORT_WARNING', (data) => {
  if (state.exportEstimatedFinishTime && data.extendedProcessingTimeMs) {
    const extendedTime = new Date(state.exportEstimatedFinishTime);
    extendedTime.setTime(
      extendedTime.getTime() + data.extendedProcessingTimeMs,
    );
    state.exportEstimatedFinishTime = extendedTime.toISOString();
  }

  if (data.warningMessage) {
    setExportErrors([data.warningMessage]);
  }
});

webSocketStore.on('EXPORT_FAILED', (data) => {
  if (data.errorMessages) {
    setExportErrors(data.errorMessages);
  }
  state.exportEstimatedFinishTime = new Date().toISOString();
  state.loading.export = false;
});
